import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Calendar,
  Clock,
  Users,
  CheckCircle,
  AlertCircle,
  ShoppingBag,
  FileText,
  CreditCard,
  Check,
  Package,
  Image as ImageIcon,
  X,
  DollarSign,
  Filter,
  Eye,
  Home,
  Lock,
  Unlock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';

// Stripe promise
let stripePromise = null;

const getStripePromise = async () => {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  if (!stripePromise) {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/stripe/publishable-key`);
      if (response.data.success) {
        stripePromise = loadStripe(response.data.publishable_key);
      }
    } catch (error) {
      console.error('Error loading Stripe key:', error);
    }
  }
  return stripePromise;
};

// Payment Modal Component
const PaymentModal = ({ show, onClose, amount, onPaymentSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (pmError) {
        setError(pmError.message);
        setProcessing(false);
        return;
      }

      await onPaymentSuccess(paymentMethod);
      setProcessing(false);
      onClose();
    } catch (err) {
      setError(err.message || 'Payment failed');
      setProcessing(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 border border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[rgb(41,173,211)]" />
              Complete Payment
            </h3>
            <p className="text-sm text-gray-400 mt-1">Token Amount: ${amount.toFixed(2)}</p>
          </div>
          <button
            onClick={onClose}
            disabled={processing}
            className="text-gray-400 hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handlePayment} className="space-y-4">
          <div className="bg-blue-900 bg-opacity-30 border border-theme-blue rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-200 text-center">
              Pay Token Amount to Book Appointment
            </p>
          </div>
          
          <div className="bg-gray-750 p-4 rounded-lg border border-gray-600">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Card Details *
            </label>
            <div className="bg-gray-700 p-3 rounded border border-gray-600">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#ffffff',
                      '::placeholder': {
                        color: '#9CA3AF',
                      },
                    },
                    invalid: {
                      color: '#ef4444',
                    },
                  },
                }}
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-900 bg-opacity-20 border border-red-500 rounded-lg p-3">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={processing}
              className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing || !stripe}
              className="flex-1 px-4 py-2 bg-[rgb(41,173,211)] text-white rounded-md hover:bg-[rgb(36,151,184)] font-medium disabled:opacity-50"
            >
              {processing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BookAppointment = () => {
  const { user, token } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);
  
  const [completedTabs, setCompletedTabs] = useState([]);
  const [serviceProviders, setServiceProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [selectedDate, setSelectedDate] = useState('today');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [servicesEnabled, setServicesEnabled] = useState(false);
  const [menuCategories, setMenuCategories] = useState([]);
  const [selectedMenuCategory, setSelectedMenuCategory] = useState('all');
  const [serviceCategories, setServiceCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [selectedService, setSelectedService] = useState(null);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [paymentSettings, setPaymentSettings] = useState({});
  const [communicationSettings, setCommunicationSettings] = useState({});
  const [onlineOrderingEnabled, setOnlineOrderingEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [modalService, setModalService] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [slotMessage, setSlotMessage] = useState({ type: '', text: '' });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmedAppointment, setConfirmedAppointment] = useState(null);
  const [stripePromiseState, setStripePromiseState] = useState(null);
  
  // Blocked slots state
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedSlotForBlock, setSelectedSlotForBlock] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [selectedSlotForUnblock, setSelectedSlotForUnblock] = useState(null);
  const [blockedSlots, setBlockedSlots] = useState([]);

  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const primaryColor = 'var(--theme-primary)';

  const tabs = [
    { id: 0, title: 'Select Time', icon: Clock },
    { id: 1, title: 'Select Service', icon: ShoppingBag },
    { id: 2, title: 'Your Details', icon: Users },
    { id: 3, title: 'Review', icon: FileText }
  ];

  const dateOptions = [
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' }
  ];

  // Generate next 7 days
  for (let i = 2; i < 9; i++) {
    const date = new Date();
    date.setDate(date.getDate() + i);
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    dateOptions.push({
      value: date.toISOString().split('T')[0],
      label: `${dayName} (${monthDay})`
    });
  }

  useEffect(() => {
    getStripePromise().then(setStripePromiseState);
  }, []);

  useEffect(() => {
    if (token) {
      fetchServiceProviders();
      fetchCustomFields();
      fetchServicesToggle();
      fetchMenuCategories();
      fetchServiceCategories();
      fetchServices();
      fetchPaymentSettings();
      fetchCommunicationSettings();
      checkOnlineOrderingEnabled();
    }
  }, [token]);

  // Filter services based on selected menu category and service category
  useEffect(() => {
    let filtered = services;
    
    // Filter by menu category first
    if (selectedMenuCategory !== 'all') {
      filtered = filtered.filter(s => s.menu_category_id === selectedMenuCategory);
    }
    
    // Then filter by service category if selected
    if (selectedCategoryFilter !== 'all') {
      filtered = filtered.filter(s => s.service_category_id === selectedCategoryFilter);
    }
    
    setFilteredServices(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [selectedMenuCategory, selectedCategoryFilter, services]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredServices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentServices = filteredServices.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Re-fetch service categories when menu category changes
  useEffect(() => {
    if (selectedMenuCategory && token) {
      fetchServiceCategories();
      setSelectedCategoryFilter('all'); // Reset service category filter
    }
  }, [selectedMenuCategory, token]);

  useEffect(() => {
    if (selectedProviderId && selectedDate) {
      fetchAvailableSlots();
      fetchBlockedSlots();
    }
  }, [selectedProviderId, selectedDate]);
  
  // Handler for date change - show provider modal if multiple providers
  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    setSelectedSlot(null);
    setCurrentTab(0); // Go back to slot selection tab
    
    // Show provider modal if multiple providers available
    if (serviceProviders.length > 1) {
      setShowProviderModal(true);
    }
  };

  const fetchServiceProviders = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/service-providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.service_providers.length > 0) {
        const enabled = response.data.service_providers.filter(sp => sp.is_enabled !== false);
        setServiceProviders(enabled);
        if (enabled.length > 0) {
          // If multiple providers, show modal. If only one, select it directly
          if (enabled.length > 1) {
            setShowProviderModal(true);
            setSelectedProviderId(''); // Don't auto-select
          } else {
            setSelectedProviderId(enabled[0].id);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching service providers:', error);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-custom-fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCustomFields(response.data.fields);
        const initialData = {};
        response.data.fields.forEach(field => {
          initialData[field.field_key] = '';
        });
        setFormData(initialData);
      }
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    }
  };

  const fetchServicesToggle = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-services-toggle`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setServicesEnabled(response.data.settings.services_enabled);
      }
    } catch (error) {
      console.error('Error fetching services toggle:', error);
    }
  };

  const fetchServiceCategories = async () => {
    try {
      // Fetch service categories based on selected menu category
      const url = selectedMenuCategory && selectedMenuCategory !== 'all' 
        ? `${API_BASE_URL}/api/appointment-service-categories-v2?menu_category_id=${selectedMenuCategory}`
        : `${API_BASE_URL}/api/appointment-service-categories-v2`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.categories) {
        const enabled = response.data.categories.filter(c => c.is_enabled);
        setServiceCategories(enabled);
      }
    } catch (error) {
      console.error('Error fetching service categories:', error);
    }
  };

  const fetchMenuCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-service-menu-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.menu_categories) {
        const enabled = response.data.menu_categories.filter(c => c.is_enabled);
        setMenuCategories(enabled);
        // Set "All Services" as default
        setSelectedMenuCategory('all');
      }
    } catch (error) {
      console.error('Error fetching menu categories:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const enabled = response.data.services.filter(s => s.is_enabled);
        setServices(enabled);
        setFilteredServices(enabled);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-payment-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setPaymentSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };

  const fetchCommunicationSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-communication-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCommunicationSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching communication settings:', error);
    }
  };

  const checkOnlineOrderingEnabled = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/menu-categories`);
      setOnlineOrderingEnabled(response.data && response.data.length > 0);
    } catch (error) {
      console.error('Error checking online ordering:', error);
      setOnlineOrderingEnabled(false);
    }
  };

  const getDateForFilter = (filterValue) => {
    const today = new Date();
    if (filterValue === 'today') {
      return today.toISOString().split('T')[0];
    } else if (filterValue === 'tomorrow') {
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    }
    return filterValue;
  };

  const fetchAvailableSlots = async () => {
    setLoading(true);
    setSlotMessage({ type: '', text: '' }); // Clear previous slot messages
    try {
      const dateStr = getDateForFilter(selectedDate);
      const response = await axios.get(
        `${API_BASE_URL}/api/service-provider-availability/${selectedProviderId}/${dateStr}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success && response.data.available) {
        let slots = response.data.slots;
        const originalSlotsCount = slots.length;
        
        // Filter slots for today based on current time
        if (selectedDate === 'today') {
          const now = new Date();
          const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes
          
          slots = slots.filter(slot => {
            const [hours, minutes] = slot.split(':').map(Number);
            const slotTime = hours * 60 + minutes;
            return slotTime > currentTime; // Only show future slots
          });
          
          // If all slots were filtered out because they're in the past
          if (originalSlotsCount > 0 && slots.length === 0) {
            setSlotMessage({ 
              type: 'info', 
              text: 'No appointments are available today as business is closed now - check for other dates' 
            });
          }
        }
        
        setAvailableSlots(slots);
        
        // Show helpful message if no slots available
        if (slots.length === 0 && originalSlotsCount === 0) {
          const dateLabel = selectedDate === 'today' ? 'today' : selectedDate === 'tomorrow' ? 'tomorrow' : 'on this date';
          setSlotMessage({ 
            type: 'info', 
            text: `No appointments are available ${dateLabel} as business is closed - check for other dates` 
          });
        }
      } else {
        setAvailableSlots([]);
        // Check if it's a provider-specific issue or business closure
        const dateLabel = selectedDate === 'today' ? 'today' : selectedDate === 'tomorrow' ? 'tomorrow' : 'on this date';
        const isClosed = response.data.message?.toLowerCase().includes('closed') || 
                        response.data.message?.toLowerCase().includes('not available') ||
                        response.data.message?.toLowerCase().includes('not working');
        
        if (isClosed) {
          setSlotMessage({ 
            type: 'info', 
            text: `No appointments are available ${dateLabel} as business is closed - check for other dates` 
          });
        } else {
          // Provider-specific issue
          setSlotMessage({ 
            type: 'info', 
            text: `This service provider has no available appointments ${dateLabel}. Please select another provider or try a different date.` 
          });
        }
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      setSlotMessage({ 
        type: 'info', 
        text: 'This service provider has no available appointments. Please select another provider or try a different date.' 
      });
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedSlots = async () => {
    try {
      const dateStr = getDateForFilter(selectedDate);
      const response = await axios.get(
        `${API_BASE_URL}/api/blocked-slots/${dateStr}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBlockedSlots(response.data || []);
    } catch (error) {
      console.error('Error fetching blocked slots:', error);
      setBlockedSlots([]);
    }
  };

  const openBlockModal = (slotTime) => {
    setSelectedSlotForBlock({ time: slotTime });
    setBlockReason('');
    setShowBlockModal(true);
  };

  const closeBlockModal = () => {
    setShowBlockModal(false);
    setSelectedSlotForBlock(null);
    setBlockReason('');
  };

  const handleBlockSlot = async () => {
    if (!selectedSlotForBlock || !selectedProviderId) return;

    try {
      const dateStr = getDateForFilter(selectedDate);
      await axios.post(
        `${API_BASE_URL}/api/blocked-slots`,
        {
          service_provider_id: selectedProviderId,
          date: dateStr,
          time: selectedSlotForBlock.time,
          reason: blockReason || 'Blocked by admin'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      closeBlockModal();
      fetchBlockedSlots();
      setMessage({ type: 'success', text: 'Time slot blocked successfully' });
    } catch (error) {
      console.error('Error blocking slot:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to block time slot' });
    }
  };

  const openUnblockModal = (blockedSlot) => {
    setSelectedSlotForUnblock(blockedSlot);
    setShowUnblockModal(true);
  };

  const closeUnblockModal = () => {
    setShowUnblockModal(false);
    setSelectedSlotForUnblock(null);
  };

  const handleUnblockSlot = async () => {
    if (!selectedSlotForUnblock) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/api/blocked-slots/${selectedSlotForUnblock.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      closeUnblockModal();
      fetchBlockedSlots();
      setMessage({ type: 'success', text: 'Time slot unblocked successfully' });
    } catch (error) {
      console.error('Error unblocking slot:', error);
      setMessage({ type: 'error', text: 'Failed to unblock time slot' });
    }
  };

  const isSlotBlocked = (slotTime) => {
    return blockedSlots.some(blocked => 
      blocked.time === slotTime && 
      blocked.service_provider_id === selectedProviderId
    );
  };

  const handleBookAppointment = async (paymentMethod = null) => {
    setLoading(true);
    try {
      const bookingData = {
        service_provider_id: selectedProviderId,
        booking_date: getDateForFilter(selectedDate),
        booking_time: selectedSlot,
        customer_details: formData,
        selected_service_id: selectedService?.id || null,
        payment_token_amount: paymentSettings.payment_enabled ? paymentSettings.token_amount : null,
        payment_status: paymentSettings.payment_enabled ? (paymentMethod ? 'completed' : 'pending') : 'not_required',
        payment_method_id: paymentMethod?.id || null
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/appointment-bookings`,
        bookingData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data) {
        setConfirmedAppointment({
          ...response.data,
          booking_date: getDateForFilter(selectedDate),
          booking_time: selectedSlot,
          service_provider_name: serviceProviders.find(p => p.id === selectedProviderId)?.full_name,
          customer_details: formData,
          selected_service: selectedService,
          payment_amount: paymentSettings.payment_enabled ? paymentSettings.token_amount : 0
        });
        setShowConfirmationModal(true);
        setMessage({ type: 'success', text: 'Appointment booked successfully!' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to book appointment' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentMethod) => {
    await handleBookAppointment(paymentMethod);
  };

  const handleConfirmBooking = () => {
    if (paymentSettings.payment_enabled) {
      setShowPaymentModal(true);
    } else {
      handleBookAppointment();
    }
  };

  const validateCurrentTab = () => {
    if (currentTab === 0) return selectedSlot !== null;
    if (currentTab === 1) return !servicesEnabled || selectedService !== null;
    if (currentTab === 2) {
      return customFields.every(field => {
        if (field.is_required) {
          return formData[field.field_key] && formData[field.field_key].trim() !== '';
        }
        return true;
      });
    }
    if (currentTab === 3) return true;
    return false;
  };

  const goToNextTab = () => {
    if (validateCurrentTab()) {
      if (!completedTabs.includes(currentTab)) {
        setCompletedTabs([...completedTabs, currentTab]);
      }
      // Skip service tab if services not enabled
      if (currentTab === 0 && !servicesEnabled) {
        setCurrentTab(2);
        if (!completedTabs.includes(1)) {
          setCompletedTabs([...completedTabs, currentTab, 1]);
        }
      } else {
        setCurrentTab(currentTab + 1);
      }
      setMessage({ type: '', text: '' });
      // Scroll to top after a short delay to ensure new content is rendered
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      setMessage({ type: 'error', text: 'Please complete all required fields' });
    }
  };

  const goToTab = (tabIndex) => {
    if (tabIndex === 0 || completedTabs.includes(tabIndex - 1)) {
      setCurrentTab(tabIndex);
      setMessage({ type: '', text: '' });
      // Scroll to top after a short delay to ensure new content is rendered
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const isTabEnabled = (tabIndex) => {
    // Skip service tab if not enabled
    if (tabIndex === 1 && !servicesEnabled) {
      return completedTabs.includes(0);
    }
    return tabIndex === 0 || completedTabs.includes(tabIndex - 1);
  };

  const isTabCompleted = (tabIndex) => {
    return completedTabs.includes(tabIndex);
  };

  const prevStep = () => {
    if (currentTab > 0) {
      // Skip service tab if not enabled
      if (currentTab === 2 && !servicesEnabled) {
        setCurrentTab(0);
      } else {
        setCurrentTab(currentTab - 1);
      }
      setMessage({ type: '', text: '' });
      // Scroll to top after a short delay to ensure new content is rendered
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour12 = parseInt(hours) % 12 || 12;
    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (currentTab === 5) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Appointment Booked!</h2>
          <p className="text-gray-600 mb-6">
            Your appointment has been successfully scheduled.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-6 py-3 text-white rounded-lg font-medium transition-colors"
            style={{ backgroundColor: primaryColor }}
          >
            Book Another Appointment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Book Appointment
              </h2>
              {currentTab === 0 && (
                <div className="flex items-center gap-2">
                  <select
                    value={selectedProviderId}
                    onChange={(e) => {
                      setSelectedProviderId(e.target.value);
                      setSelectedSlot(null);
                    }}
                    className="text-white text-sm pl-3 pr-8 py-1.5 rounded-md font-medium focus:outline-none"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {serviceProviders.map((provider) => (
                      <option key={provider.id} value={provider.id} style={{ backgroundColor: 'white', color: 'black' }}>
                        {provider.full_name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedDate}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="text-white text-sm pl-3 pr-8 py-1.5 rounded-md font-medium focus:outline-none"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {dateOptions.map((option) => (
                      <option key={option.value} value={option.value} style={{ backgroundColor: 'white', color: 'black' }}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="p-6">
            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex px-6" aria-label="Tabs">
                  {tabs.map((tab) => {
                    // Skip service tab in UI if not enabled
                    if (tab.id === 1 && !servicesEnabled) return null;
                    
                    const IconComponent = tab.icon;
                    const isActive = currentTab === tab.id;
                    const isCompleted = isTabCompleted(tab.id);
                    const isEnabled = isTabEnabled(tab.id);
                    
                    return (
                      <button
                        key={tab.id}
                        onClick={() => goToTab(tab.id)}
                        disabled={!isEnabled}
                        className={`flex-1 py-4 px-1 border-b-2 font-medium text-sm flex items-center justify-center space-x-2 ${
                          isActive
                            ? 'border-theme-blue text-theme-blue'
                            : isCompleted
                            ? 'border-theme-blue text-theme-blue'
                            : isEnabled
                            ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            : 'border-transparent text-gray-300 cursor-not-allowed'
                        }`}
                      >
                        {isCompleted ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <IconComponent className="h-4 w-4" />
                        )}
                        <span>{tab.title}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {message.text && (
                  <div className={`mb-6 p-4 rounded-lg flex items-start ${
                    message.type === 'success' ? 'border' : 
                    message.type === 'info' ? 'bg-blue-50 border border-blue-200' :
                    'bg-red-50 border border-red-200'
                  }`}
                  style={message.type === 'success' ? {
                    backgroundColor: 'var(--theme-primary-light)',
                    borderColor: primaryColor
                  } : {}}
                  >
                    {message.type === 'info' ? (
                      <AlertCircle className="h-5 w-5 mr-3 mt-0.5 text-theme-blue flex-shrink-0" />
                    ) : (
                      <AlertCircle className={`h-5 w-5 mr-3 mt-0.5 flex-shrink-0 ${
                        message.type === 'success' ? '' : 'text-red-600'
                      }`} 
                      style={message.type === 'success' ? { color: primaryColor } : {}}
                      />
                    )}
                    <p className={`${
                      message.type === 'success' ? '' : 
                      message.type === 'info' ? 'text-blue-800' :
                      'text-red-800'
                    }`}
                    style={message.type === 'success' ? { color: '#1e7a8c' } : {}}
                    >
                      {message.text}
                    </p>
                  </div>
                )}

                {/* Tab 0: Time Selection */}
                {currentTab === 0 && (
                  <div>
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: primaryColor }}></div>
                        <p className="mt-2 text-gray-600">Loading available times...</p>
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <div className="text-center py-12 px-4">
                        <div className="max-w-md mx-auto">
                          <Clock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                          {slotMessage.text ? (
                            <p className="text-gray-700 text-sm leading-relaxed">{slotMessage.text}</p>
                          ) : (
                            <p className="text-gray-600">No available time slots</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {availableSlots.map((slot) => {
                          const blocked = isSlotBlocked(slot.time);
                          const blockedSlot = blockedSlots.find(b => b.time === slot.time && b.service_provider_id === selectedProviderId);
                          
                          return (
                            <div key={slot.time} className="relative">
                              <button
                                onClick={() => {
                                  if (!blocked) {
                                    setSelectedSlot(slot.time);
                                    // Scroll to Continue button
                                    setTimeout(() => {
                                      const navButtons = document.getElementById('navigation-buttons');
                                      if (navButtons) {
                                        navButtons.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      }
                                    }, 100);
                                  }
                                }}
                                disabled={blocked}
                                className={`w-full p-4 rounded-lg border-2 text-center transition-all ${
                                  blocked
                                    ? 'border-red-300 bg-red-50 cursor-not-allowed opacity-60'
                                    : selectedSlot === slot.time
                                    ? 'border-theme-blue bg-blue-50 shadow-md'
                                    : 'border-gray-200 hover:border-blue-300 hover:shadow'
                                }`}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  {blocked && <Lock className="h-3 w-3 text-red-600" />}
                                  <div className="text-sm font-semibold text-gray-900">
                                    {formatTime(slot.time)}
                                  </div>
                                </div>
                                {blocked && blockedSlot?.reason && (
                                  <div className="text-xs text-red-600 mt-1 truncate" title={blockedSlot.reason}>
                                    {blockedSlot.reason}
                                  </div>
                                )}
                              </button>
                              
                              {/* Block/Unblock button for logged-in users */}
                              {user && (user.role === 'admin' || user.role === 'manager') && (
                                <button
                                  onClick={() => blocked ? openUnblockModal(blockedSlot) : openBlockModal(slot.time)}
                                  className={`absolute -top-2 -right-2 p-1.5 rounded-full shadow-md transition-all ${
                                    blocked 
                                      ? 'bg-green-500 hover:bg-green-600' 
                                      : 'bg-red-500 hover:bg-red-600'
                                  }`}
                                  title={blocked ? 'Unblock this slot' : 'Block this slot'}
                                >
                                  {blocked ? (
                                    <Unlock className="h-3 w-3 text-white" />
                                  ) : (
                                    <Lock className="h-3 w-3 text-white" />
                                  )}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Tab 1: Service Selection */}
                {currentTab === 1 && servicesEnabled && (
                  <div>
                    {/* Menu Category Tabs and Filter */}
                    <div className="mb-4">
                      {/* Menu Category Tabs with Filter Button */}
                      {menuCategories.length > 0 && (
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-1">
                            <button
                              onClick={() => setSelectedMenuCategory('all')}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                selectedMenuCategory === 'all'
                                  ? 'text-white shadow-md'
                                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                              }`}
                              style={selectedMenuCategory === 'all' ? { backgroundColor: primaryColor } : {}}
                            >
                              All Services
                            </button>
                            {menuCategories.map((category) => (
                              <button
                                key={category.id}
                                onClick={() => setSelectedMenuCategory(category.id)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                  selectedMenuCategory === category.id
                                    ? 'text-white shadow-md'
                                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                }`}
                                style={selectedMenuCategory === category.id ? { backgroundColor: primaryColor } : {}}
                              >
                                {category.menu_category_name}
                              </button>
                            ))}
                          </div>
                          
                          {/* Filter Button */}
                          {serviceCategories.length > 0 && (
                            <button
                              onClick={() => setShowCategoryFilter(!showCategoryFilter)}
                              className="flex items-center justify-center p-2 text-white rounded-lg shadow-md flex-shrink-0"
                              style={{ backgroundColor: primaryColor }}
                            >
                              <Filter className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Filter Dropdown */}
                    {showCategoryFilter && (
                      <div className="mb-4 bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                        <div className="space-y-2">
                          <button
                            onClick={() => {
                              setSelectedCategoryFilter('all');
                              setShowCategoryFilter(false);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                              selectedCategoryFilter === 'all'
                                ? 'bg-gray-200 text-gray-900 font-semibold'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                            }`}
                          >
                            All Categories
                          </button>
                          {serviceCategories.map((cat) => (
                            <button
                              key={cat.id}
                              onClick={() => {
                                setSelectedCategoryFilter(cat.id);
                                setShowCategoryFilter(false);
                              }}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                                selectedCategoryFilter === cat.id
                                  ? 'bg-gray-200 text-gray-900 font-semibold'
                                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                              }`}
                            >
                              {cat.category_name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Services Grid */}
                    {filteredServices.length === 0 ? (
                      <div className="text-center py-8">
                        <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600">No services available</p>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {currentServices.map((service) => (
                          <div
                            key={service.id}
                            className={`rounded-lg border-2 overflow-hidden transition-all flex ${
                              selectedService?.id === service.id
                                ? 'border-theme-blue bg-blue-50 shadow-md'
                                : 'border-gray-200 hover:border-blue-300 hover:shadow'
                            }`}
                          >
                            {/* Service Image - Left Side */}
                            <div className="w-40 h-40 flex-shrink-0 bg-gray-50 relative group">
                              {service.image_url ? (
                                <>
                                  <img
                                    src={service.image_url}
                                    alt={service.service_name}
                                    className="w-full h-full object-contain"
                                  />
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPreviewImageUrl(service.image_url);
                                      setShowImagePreview(true);
                                    }}
                                    className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    type="button"
                                  >
                                    <Eye className="h-8 w-8 text-white" />
                                  </button>
                                </>
                              ) : (
                                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                  <ImageIcon className="h-12 w-12 text-gray-400" />
                                </div>
                              )}
                            </div>
                            
                            {/* Service Details - Right Side */}
                            <div className="flex-1 p-3 flex flex-col">
                              <h4 className="font-semibold text-gray-900 text-sm mb-1.5">{service.service_name}</h4>
                              <div className="space-y-1.5 mb-2 flex-1">
                                {/* Description */}
                                {service.description && (
                                  <p className="text-xs text-gray-500 line-clamp-2">{service.description}</p>
                                )}
                                
                                {/* Price and Duration on same row */}
                                <div className="flex items-center justify-between">
                                  <div>
                                    {service.price && !service.is_variable && (
                                      <p className="text-sm font-semibold text-gray-900">
                                        ${service.price.toFixed(2)}
                                      </p>
                                    )}
                                    {service.is_variable && service.variants && service.variants.length > 0 && (
                                      <p className="text-sm font-semibold text-gray-900">
                                        From ${Math.min(...service.variants.map(v => v.price)).toFixed(2)}
                                      </p>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-600 flex items-center">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {service.duration_minutes} min
                                  </p>
                                </div>
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="flex gap-2 mt-auto">
                                <button
                                  onClick={() => {
                                    setModalService(service);
                                    setShowServiceModal(true);
                                    // Pre-populate with existing selections if this service is already selected
                                    if (selectedService?.id === service.id) {
                                      setSelectedVariant(selectedService.selectedVariant || null);
                                      setSelectedOptions(selectedService.selectedOptions || {});
                                    } else {
                                      setSelectedVariant(null);
                                      setSelectedOptions({});
                                    }
                                  }}
                                  className="flex-1 px-2 py-1.5 text-xs font-medium border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                                >
                                  See Details
                                </button>
                                <button
                                  onClick={() => {
                                    // Quick select without options/variants
                                    const serviceWithSelections = {
                                      ...service,
                                      selectedVariant: null,
                                      selectedOptions: {}
                                    };
                                    setSelectedService(serviceWithSelections);
                                    // Scroll to Continue button
                                    setTimeout(() => {
                                      const navButtons = document.getElementById('navigation-buttons');
                                      if (navButtons) {
                                        navButtons.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      }
                                    }, 100);
                                  }}
                                  className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-colors ${
                                    selectedService?.id === service.id
                                      ? 'bg-theme-blue text-white'
                                      : 'text-white'
                                  }`}
                                  style={{ backgroundColor: selectedService?.id === service.id ? undefined : primaryColor }}
                                >
                                  {selectedService?.id === service.id ? 'Selected' : 'Select'}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                          <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-sm text-gray-700">
                                <span>
                                  Showing {startIndex + 1} to {Math.min(endIndex, filteredServices.length)} of {filteredServices.length} services
                                </span>
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={goToPreviousPage}
                                  disabled={currentPage === 1}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Previous
                                </button>
                                
                                <div className="flex space-x-1">
                                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
                                    <button
                                      key={pageNumber}
                                      onClick={() => goToPage(pageNumber)}
                                      className={`px-3 py-2 text-sm font-medium rounded-lg ${
                                        currentPage === pageNumber
                                          ? 'text-white'
                                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                      }`}
                                      style={currentPage === pageNumber ? { backgroundColor: primaryColor } : {}}
                                    >
                                      {pageNumber}
                                    </button>
                                  ))}
                                </div>
                                
                                <button
                                  onClick={goToNextPage}
                                  disabled={currentPage === totalPages}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Tab 2: Customer Details */}
                {currentTab === 2 && (
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl">
                      {customFields.map((field) => (
                        <div key={field.id}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {field.field_name}
                            {field.is_required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {field.field_type === 'text' ? (
                            <input
                              type="text"
                              value={formData[field.field_key] || ''}
                              onChange={(e) => setFormData({ ...formData, [field.field_key]: e.target.value })}
                              required={field.is_required}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                              style={{ focusRingColor: primaryColor }}
                            />
                          ) : field.field_type === 'email' ? (
                            <input
                              type="email"
                              value={formData[field.field_key] || ''}
                              onChange={(e) => setFormData({ ...formData, [field.field_key]: e.target.value })}
                              required={field.is_required}
                              placeholder="example@email.com"
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                              style={{ focusRingColor: primaryColor }}
                            />
                          ) : field.field_type === 'phone' ? (
                            <input
                              type="tel"
                              value={formData[field.field_key] || ''}
                              onChange={(e) => setFormData({ ...formData, [field.field_key]: e.target.value })}
                              required={field.is_required}
                              placeholder="(123) 456-7890"
                              pattern="[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}"
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                              style={{ focusRingColor: primaryColor }}
                            />
                          ) : field.field_type === 'date' ? (
                            <input
                              type="date"
                              value={formData[field.field_key] || ''}
                              onChange={(e) => setFormData({ ...formData, [field.field_key]: e.target.value })}
                              required={field.is_required}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                              style={{ focusRingColor: primaryColor }}
                            />
                          ) : field.field_type === 'number' ? (
                            <input
                              type="number"
                              value={formData[field.field_key] || ''}
                              onChange={(e) => setFormData({ ...formData, [field.field_key]: e.target.value })}
                              required={field.is_required}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                              style={{ focusRingColor: primaryColor }}
                            />
                          ) : field.field_type === 'textarea' ? (
                            <textarea
                              value={formData[field.field_key] || ''}
                              onChange={(e) => setFormData({ ...formData, [field.field_key]: e.target.value })}
                              required={field.is_required}
                              rows={3}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                              style={{ focusRingColor: primaryColor }}
                            />
                          ) : field.field_type === 'dropdown' ? (
                            <select
                              value={formData[field.field_key] || ''}
                              onChange={(e) => setFormData({ ...formData, [field.field_key]: e.target.value })}
                              required={field.is_required}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                              style={{ focusRingColor: primaryColor }}
                            >
                              <option value="">Select...</option>
                              {field.dropdown_options?.map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tab 3: Review */}
                {currentTab === 3 && (
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="text-sm font-medium text-gray-700 mb-2">Date</div>
                        <div className="text-sm text-gray-900">
                          {dateOptions.find(d => d.value === selectedDate)?.label}
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="text-sm font-medium text-gray-700 mb-2">Time</div>
                        <div className="text-sm text-gray-900">
                          {formatTime(selectedSlot)}
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="text-sm font-medium text-gray-700 mb-2">Service Provider</div>
                        <div className="text-sm text-gray-900">
                          {serviceProviders.find(p => p.id === selectedProviderId)?.full_name}
                        </div>
                      </div>

                      {selectedService && (
                        <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200 md:col-span-2">
                          <div className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                            <Package className="h-4 w-4" />
                            Service Details
                          </div>
                          <div className="space-y-3">
                            {/* Service Name and Duration */}
                            <div>
                              <div className="text-base text-gray-900 font-bold">
                                {selectedService.service_name}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                <Clock className="h-3 w-3 inline mr-1" />
                                Duration: {selectedService.duration_minutes} minutes
                              </div>
                            </div>
                            
                            {/* Service Description */}
                            {selectedService.description && (
                              <div className="text-sm text-gray-700 bg-white p-3 rounded border border-blue-100">
                                {selectedService.description}
                              </div>
                            )}
                            
                            {/* Pricing Details */}
                            <div className="border-t border-blue-200 pt-3 space-y-2">
                              {/* Selected Variant */}
                              {selectedService.selectedVariant && (
                                <div className="flex items-center justify-between bg-white p-3 rounded">
                                  <div>
                                    <div className="text-xs text-gray-600">Selected Option</div>
                                    <div className="text-sm text-gray-900 font-medium">
                                      {selectedService.selectedVariant.variant_type}: {selectedService.selectedVariant.variant_name}
                                    </div>
                                  </div>
                                  <div className="text-base font-bold text-green-700">
                                    ${selectedService.selectedVariant.price.toFixed(2)}
                                  </div>
                                </div>
                              )}
                              
                              {/* Fixed Price if no variant */}
                              {!selectedService.selectedVariant && selectedService.price && (
                                <div className="flex items-center justify-between bg-white p-3 rounded">
                                  <span className="text-sm text-gray-600">Service Price</span>
                                  <div className="text-base font-bold text-green-700">
                                    ${selectedService.price.toFixed(2)}
                                  </div>
                                </div>
                              )}
                              
                              {/* Additional Options */}
                              {selectedService.selectedOptions && Object.keys(selectedService.selectedOptions).length > 0 && (
                                <div className="bg-white p-3 rounded">
                                  <div className="text-xs font-semibold text-gray-600 mb-2">Additional Options:</div>
                                  <div className="space-y-2">
                                    {Object.entries(selectedService.selectedOptions).map(([key, value]) => {
                                      if (!value) return null;
                                      
                                      // Find the option details
                                      const option = selectedService.options?.find(opt => opt.option_name === key);
                                      if (!option) return null;
                                      
                                      // For checkbox (single) options
                                      if (option.option_type === 'single' && value === true) {
                                        return (
                                          <div key={key} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2 last:border-0">
                                            <span className="text-gray-700">• {key}</span>
                                            {option.price && (
                                              <span className="text-green-700 font-medium">+${option.price.toFixed(2)}</span>
                                            )}
                                          </div>
                                        );
                                      }
                                      
                                      // For dropdown options
                                      if (option.option_type === 'dropdown' && value) {
                                        const choice = option.choices?.find(c => c.choice_name === value);
                                        return (
                                          <div key={key} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2 last:border-0">
                                            <span className="text-gray-700">• {key}: <span className="font-medium">{value}</span></span>
                                            {choice?.price && (
                                              <span className="text-green-700 font-medium">+${choice.price.toFixed(2)}</span>
                                            )}
                                          </div>
                                        );
                                      }
                                      
                                      return null;
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Total Calculation */}
                              {(selectedService.selectedVariant || selectedService.price) && (
                                <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-blue-50 p-3 rounded border-2 border-green-200">
                                  <span className="text-sm font-bold text-gray-900">Total Service Cost</span>
                                  <div className="text-lg font-bold text-green-700">
                                    ${(() => {
                                      let total = 0;
                                      // Add base price
                                      if (selectedService.selectedVariant) {
                                        total += selectedService.selectedVariant.price;
                                      } else if (selectedService.price) {
                                        total += selectedService.price;
                                      }
                                      // Add option prices
                                      if (selectedService.selectedOptions) {
                                        Object.entries(selectedService.selectedOptions).forEach(([key, value]) => {
                                          const option = selectedService.options?.find(opt => opt.option_name === key);
                                          if (option) {
                                            if (option.option_type === 'single' && value === true && option.price) {
                                              total += option.price;
                                            } else if (option.option_type === 'dropdown' && value) {
                                              const choice = option.choices?.find(c => c.choice_name === value);
                                              if (choice?.price) {
                                                total += choice.price;
                                              }
                                            }
                                          }
                                        });
                                      }
                                      return total.toFixed(2);
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="p-4 bg-gray-50 rounded-lg md:col-span-2">
                        <div className="text-sm font-medium text-gray-700 mb-2">Your Details</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                          {customFields
                            .filter(field => formData[field.field_key])
                            .map((field) => (
                              <div key={field.id}>
                                <span className="text-sm text-gray-600">{field.field_name}: </span>
                                <span className="text-sm text-gray-900">{formData[field.field_key]}</span>
                              </div>
                            ))}
                        </div>
                      </div>

                      {paymentSettings.payment_enabled && (
                        <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200 md:col-span-2">
                          <div className="flex items-center gap-2 mb-2">
                            <CreditCard className="h-4 w-4 text-theme-blue" />
                            <div className="text-sm font-medium text-blue-900">Payment Required</div>
                          </div>
                          <div className="text-sm text-gray-700">
                            Token Amount: ${paymentSettings.token_amount?.toFixed(2)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div id="navigation-buttons" className="flex items-center justify-between mt-6">
              {currentTab > 0 && currentTab < 4 && (
                <button
                  onClick={prevStep}
                  className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
              )}

              {currentTab === 3 ? (
                <button
                  onClick={handleConfirmBooking}
                  disabled={loading || !validateCurrentTab()}
                  className="ml-auto px-6 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: (validateCurrentTab() && !loading) ? primaryColor : '#9ca3af' }}
                >
                  {loading ? 'Processing...' : 'Book Now'}
                </button>
              ) : currentTab < 3 && (
                <button
                  onClick={goToNextTab}
                  disabled={!validateCurrentTab()}
                  className="ml-auto px-6 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: validateCurrentTab() ? primaryColor : '#9ca3af' }}
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Service Provider Selection Modal */}
      {showProviderModal && serviceProviders.length > 1 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            {/* Header */}
            <div className="px-6 py-4 border-b" style={{ backgroundColor: primaryColor }}>
              <h3 className="text-lg font-semibold text-white">Select Service Provider</h3>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Please select a service provider to view available appointments
              </p>
              
              <div className="space-y-3">
                {serviceProviders.map((provider) => (
                  <button
                    key={provider.id}
                    onClick={() => {
                      setSelectedProviderId(provider.id);
                      setShowProviderModal(false);
                      setSelectedSlot(null);
                    }}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      selectedProviderId === provider.id
                        ? 'border-theme-blue bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:shadow'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">{provider.full_name}</h4>
                        {provider.specialization && (
                          <p className="text-sm text-gray-600 mt-1">{provider.specialization}</p>
                        )}
                      </div>
                      {selectedProviderId === provider.id && (
                        <CheckCircle className="h-6 w-6" style={{ color: primaryColor }} />
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {/* Action Button */}
              <div className="mt-6 flex gap-3">
                {selectedProviderId && (
                  <button
                    onClick={() => setShowProviderModal(false)}
                    className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Continue
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Service Details Modal */}
      {showServiceModal && modalService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-3 flex items-center justify-between border-b" style={{ backgroundColor: primaryColor }}>
              <h3 className="text-lg font-semibold text-white">Service Details</h3>
              <button
                onClick={() => {
                  setShowServiceModal(false);
                  setSelectedVariant(null);
                  setSelectedOptions({});
                }}
                className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Image and Basic Info Section */}
              <div className="flex gap-4 mb-6">
                {/* Service Image - Left Side */}
                <div className="w-40 h-40 flex-shrink-0 bg-gray-50 rounded-lg relative group">
                  {modalService.image_url ? (
                    <>
                      <img
                        src={modalService.image_url}
                        alt={modalService.service_name}
                        className="w-full h-full object-contain rounded-lg"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewImageUrl(modalService.image_url);
                          setShowImagePreview(true);
                        }}
                        className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                        type="button"
                      >
                        <Eye className="h-8 w-8 text-white" />
                      </button>
                    </>
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center rounded-lg">
                      <ImageIcon className="h-16 w-16 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Service Info - Right Side */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{modalService.service_name}</h4>
                  <div className="flex items-center gap-4 mb-2">
                    <p className="text-sm text-gray-600 flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {modalService.duration_minutes} minutes
                    </p>
                    {modalService.price && !modalService.is_variable && (
                      <p className="text-base font-bold text-gray-900 flex items-center">
                        <DollarSign className="h-4 w-4" />
                        {modalService.price.toFixed(2)}
                      </p>
                    )}
                  </div>
                  
                  {modalService.description && (
                    <p className="text-sm text-gray-600">{modalService.description}</p>
                  )}
                </div>
              </div>

              {/* Variants */}
              {modalService.is_variable && modalService.variants && modalService.variants.length > 0 && (
                <div className="mb-6">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3">Select {modalService.variants[0]?.variant_type || 'Option'}</h5>
                  <div className="space-y-2">
                    {modalService.variants.map((variant) => (
                      <button
                        key={variant.variant_name}
                        onClick={() => setSelectedVariant(variant)}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all flex items-center justify-between ${
                          selectedVariant?.variant_name === variant.variant_name
                            ? 'border-theme-blue bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <span className="font-medium text-gray-900">{variant.variant_name}</span>
                        <span className="font-semibold text-gray-900">${variant.price.toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Options */}
              {modalService.has_options && modalService.options && modalService.options.length > 0 && (
                <div className="mb-6">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3">Service Options</h5>
                  <div className="space-y-4">
                    {modalService.options.map((option, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          {option.option_name} {option.is_required && <span className="text-red-500">*</span>}
                        </label>
                        
                        {option.option_type === 'single' && (
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedOptions[option.option_name] || false}
                              onChange={(e) => setSelectedOptions({
                                ...selectedOptions,
                                [option.option_name]: e.target.checked
                              })}
                              className="mr-2"
                            />
                            <span className="text-sm text-gray-700">
                              Add {option.option_name}
                              {option.price && ` (+$${option.price})`}
                            </span>
                          </label>
                        )}
                        
                        {option.option_type === 'dropdown' && option.choices && (
                          <select
                            value={selectedOptions[option.option_name] || ''}
                            onChange={(e) => setSelectedOptions({
                              ...selectedOptions,
                              [option.option_name]: e.target.value
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="">Select {option.option_name}</option>
                            {option.choices.map((choice, cIdx) => (
                              <option key={cIdx} value={choice.choice_name}>
                                {choice.choice_name} {choice.price && `(+$${choice.price})`}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>

            {/* Footer - Sticky */}
            <div className="border-t border-gray-200 px-6 py-3 bg-white rounded-b-xl">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowServiceModal(false);
                    setSelectedVariant(null);
                    setSelectedOptions({});
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Create service object with selections
                    const serviceWithSelections = {
                      ...modalService,
                      selectedVariant: selectedVariant,
                      selectedOptions: selectedOptions
                    };
                    setSelectedService(serviceWithSelections);
                    setShowServiceModal(false);
                    // Scroll to Continue button
                    setTimeout(() => {
                      const navButtons = document.getElementById('navigation-buttons');
                      if (navButtons) {
                        navButtons.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }, 100);
                  }}
                  className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors text-sm"
                  style={{ backgroundColor: primaryColor }}
                >
                  Select & Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && stripePromiseState && (
        <Elements stripe={stripePromiseState}>
          <PaymentModal
            show={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            amount={paymentSettings.token_amount || 0}
            onPaymentSuccess={handlePaymentSuccess}
          />
        </Elements>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && confirmedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Compact Sticky Header */}
            <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h3 className="text-lg font-semibold text-white">
                Appointment Confirmed
              </h3>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Success Message */}
              <div className="text-center mb-6">
                <p className="text-sm text-gray-700 leading-relaxed">
                  Your appointment has been successfully booked.
                  {communicationSettings.email_enabled && (
                    <span className="block mt-1">A confirmation email has been sent to you.</span>
                  )}
                  {communicationSettings.sms_enabled && (
                    <span className="block mt-1">A confirmation SMS has been sent to you.</span>
                  )}
                </p>
              </div>

              {/* Appointment Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {confirmedAppointment.appointment_number && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 mb-1">Appointment Number</div>
                    <div className="text-sm text-gray-900 font-bold">{confirmedAppointment.appointment_number}</div>
                  </div>
                )}

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 mb-1">Service Provider</div>
                  <div className="text-sm text-gray-900">{confirmedAppointment.service_provider_name}</div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 mb-1">Date</div>
                  <div className="text-sm text-gray-900">
                    {new Date(confirmedAppointment.booking_date).toLocaleDateString('en-US', { 
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                    })}
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 mb-1">Time</div>
                  <div className="text-sm text-gray-900">
                    {formatTime(confirmedAppointment.booking_time)}
                  </div>
                </div>

                {/* Service Details in Styled Section */}
                {confirmedAppointment.selected_service && (
                  <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200 md:col-span-2">
                    <div className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Service Details
                    </div>
                    <div className="space-y-3">
                      {/* Service Name and Duration */}
                      <div>
                        <div className="text-base text-gray-900 font-bold">
                          {confirmedAppointment.selected_service.service_name}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          <Clock className="h-3 w-3 inline mr-1" />
                          Duration: {confirmedAppointment.selected_service.duration_minutes} minutes
                        </div>
                      </div>

                      {/* Service Description */}
                      {confirmedAppointment.selected_service.description && (
                        <div className="text-sm text-gray-700 bg-white p-3 rounded border border-blue-100">
                          {confirmedAppointment.selected_service.description}
                        </div>
                      )}

                      {/* Pricing Details */}
                      <div className="border-t border-blue-200 pt-3 space-y-2">
                        {/* Selected Variant */}
                        {confirmedAppointment.selected_service.selectedVariant && (
                          <div className="flex items-center justify-between bg-white p-3 rounded">
                            <div>
                              <div className="text-xs text-gray-600">Selected Option</div>
                              <div className="text-sm text-gray-900 font-medium">
                                {confirmedAppointment.selected_service.selectedVariant.variant_type}: {confirmedAppointment.selected_service.selectedVariant.variant_name}
                              </div>
                            </div>
                            <div className="text-base font-bold text-green-700">
                              ${confirmedAppointment.selected_service.selectedVariant.price.toFixed(2)}
                            </div>
                          </div>
                        )}

                        {/* Fixed Price if no variant */}
                        {!confirmedAppointment.selected_service.selectedVariant && confirmedAppointment.selected_service.price && (
                          <div className="flex items-center justify-between bg-white p-3 rounded">
                            <span className="text-sm text-gray-600">Service Price</span>
                            <div className="text-base font-bold text-green-700">
                              ${confirmedAppointment.selected_service.price.toFixed(2)}
                            </div>
                          </div>
                        )}

                        {/* Additional Options */}
                        {confirmedAppointment.selected_service.selectedOptions && 
                         Object.keys(confirmedAppointment.selected_service.selectedOptions).length > 0 && (
                          <div className="bg-white p-3 rounded">
                            <div className="text-xs font-semibold text-gray-600 mb-2">Additional Options:</div>
                            <div className="space-y-2">
                              {Object.entries(confirmedAppointment.selected_service.selectedOptions).map(([key, value]) => {
                                if (!value) return null;
                                const option = confirmedAppointment.selected_service.options?.find(opt => opt.option_name === key);
                                if (!option) return null;

                                if (option.option_type === 'single' && value === true) {
                                  return (
                                    <div key={key} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2 last:border-0">
                                      <span className="text-gray-700">• {key}</span>
                                      {option.price && (
                                        <span className="text-green-700 font-medium">+${option.price.toFixed(2)}</span>
                                      )}
                                    </div>
                                  );
                                }

                                if (option.option_type === 'dropdown' && value) {
                                  const choice = option.choices?.find(c => c.choice_name === value);
                                  return (
                                    <div key={key} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2 last:border-0">
                                      <span className="text-gray-700">• {key}: <span className="font-medium">{value}</span></span>
                                      {choice?.price && (
                                        <span className="text-green-700 font-medium">+${choice.price.toFixed(2)}</span>
                                      )}
                                    </div>
                                  );
                                }
                                return null;
                              })}
                            </div>
                          </div>
                        )}

                        {/* Total Service Cost */}
                        {(confirmedAppointment.selected_service.selectedVariant || confirmedAppointment.selected_service.price) && (
                          <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-blue-50 p-3 rounded border-2 border-green-200">
                            <span className="text-sm font-bold text-gray-900">Total Service Cost</span>
                            <div className="text-lg font-bold text-green-700">
                              ${(() => {
                                let total = 0;
                                if (confirmedAppointment.selected_service.selectedVariant) {
                                  total += confirmedAppointment.selected_service.selectedVariant.price;
                                } else if (confirmedAppointment.selected_service.price) {
                                  total += confirmedAppointment.selected_service.price;
                                }
                                if (confirmedAppointment.selected_service.selectedOptions) {
                                  Object.entries(confirmedAppointment.selected_service.selectedOptions).forEach(([key, value]) => {
                                    const option = confirmedAppointment.selected_service.options?.find(opt => opt.option_name === key);
                                    if (option) {
                                      if (option.option_type === 'single' && value === true && option.price) {
                                        total += option.price;
                                      } else if (option.option_type === 'dropdown' && value) {
                                        const choice = option.choices?.find(c => c.choice_name === value);
                                        if (choice?.price) {
                                          total += choice.price;
                                        }
                                      }
                                    }
                                  });
                                }
                                return total.toFixed(2);
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-4 bg-gray-50 rounded-lg md:col-span-2">
                  <div className="text-sm font-medium text-gray-700 mb-2">Your Details</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                    {customFields
                      .filter(field => confirmedAppointment.customer_details[field.field_key])
                      .map((field) => (
                        <div key={field.id}>
                          <span className="text-sm text-gray-600">{field.field_name}: </span>
                          <span className="text-sm text-gray-900">{confirmedAppointment.customer_details[field.field_key]}</span>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Payment Information with Balance Due */}
                {confirmedAppointment.payment_token_amount > 0 && (
                  <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-300 md:col-span-2">
                    <div className="text-sm font-semibold text-yellow-900 mb-3 flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Payment Information
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between bg-white p-3 rounded">
                        <span className="text-sm text-gray-600">Token Amount Paid</span>
                        <div className="text-sm font-bold text-green-700">
                          ${confirmedAppointment.payment_token_amount.toFixed(2)}
                        </div>
                      </div>
                      {(confirmedAppointment.selected_service?.selectedVariant || confirmedAppointment.selected_service?.price) && (
                        <div className="flex items-center justify-between bg-orange-50 p-3 rounded border-2 border-orange-300">
                          <span className="text-sm font-bold text-gray-900">Balance Due at Service</span>
                          <div className="text-base font-bold text-orange-700">
                            ${(() => {
                              let total = 0;
                              if (confirmedAppointment.selected_service.selectedVariant) {
                                total += confirmedAppointment.selected_service.selectedVariant.price;
                              } else if (confirmedAppointment.selected_service.price) {
                                total += confirmedAppointment.selected_service.price;
                              }
                              if (confirmedAppointment.selected_service.selectedOptions) {
                                Object.entries(confirmedAppointment.selected_service.selectedOptions).forEach(([key, value]) => {
                                  const option = confirmedAppointment.selected_service.options?.find(opt => opt.option_name === key);
                                  if (option) {
                                    if (option.option_type === 'single' && value === true && option.price) {
                                      total += option.price;
                                    } else if (option.option_type === 'dropdown' && value) {
                                      const choice = option.choices?.find(c => c.choice_name === value);
                                      if (choice?.price) {
                                        total += choice.price;
                                      }
                                    }
                                  }
                                });
                              }
                              const balance = total - confirmedAppointment.payment_token_amount;
                              return balance > 0 ? balance.toFixed(2) : '0.00';
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sticky Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-between items-center gap-3 flex-shrink-0 bg-white">
              {/* Left side - Online Store button (if enabled) */}
              {onlineOrderingEnabled ? (
                <button
                  onClick={() => {
                    setShowConfirmationModal(false);
                    navigate('/order-online');
                  }}
                  className="inline-flex items-center gap-2 px-6 py-2 text-white rounded-lg font-medium transition-colors text-sm"
                  style={{ backgroundColor: primaryColor }}
                >
                  <ShoppingBag className="h-4 w-4" />
                  Visit Online Store
                </button>
              ) : (
                <div></div>
              )}
              
              {/* Right side - Close button */}
              <button
                onClick={() => {
                  setShowConfirmationModal(false);
                  navigate('/');
                }}
                className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block Time Slot Modal */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200" style={{ backgroundColor: primaryColor }}>
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Block Time Slot
              </h3>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Block the time slot <strong>{formatTime(selectedSlotForBlock?.time)}</strong> for{' '}
                {serviceProviders.find(p => p.id === selectedProviderId)?.full_name} on{' '}
                {dateOptions.find(d => d.value === selectedDate)?.label}?
              </p>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason (Optional)
                </label>
                <textarea
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent text-sm"
                  placeholder="e.g., Personal appointment, Break time, etc."
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeBlockModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleBlockSlot}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Lock className="h-4 w-4" />
                Block Slot
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unblock Time Slot Modal */}
      {showUnblockModal && selectedSlotForUnblock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200" style={{ backgroundColor: primaryColor }}>
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Unlock className="h-5 w-5" />
                Unblock Time Slot
              </h3>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to unblock the time slot{' '}
                <strong>{formatTime(selectedSlotForUnblock.time)}</strong> for{' '}
                {serviceProviders.find(p => p.id === selectedProviderId)?.full_name} on{' '}
                {dateOptions.find(d => d.value === selectedDate)?.label}?
              </p>
              
              {selectedSlotForUnblock.reason && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-xs font-medium text-gray-500 mb-1">Block Reason:</p>
                  <p className="text-sm text-gray-900">{selectedSlotForUnblock.reason}</p>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeUnblockModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleUnblockSlot}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors text-sm font-medium flex items-center gap-2"
                style={{ backgroundColor: primaryColor }}
              >
                <Unlock className="h-4 w-4" />
                Unblock Slot
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImagePreview && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
          onClick={() => setShowImagePreview(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={previewImageUrl}
              alt="Preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setShowImagePreview(false)}
              className="absolute top-3 right-3 p-2 bg-black bg-opacity-60 rounded hover:bg-opacity-80 transition-all"
              type="button"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookAppointment;
